<template>
  <div class="admin-container">
    <v-row>
      <h2 class="admin-title">{{ $t('admin.title') }}</h2>
    </v-row>
    <v-row>
      <v-card class="admin-card">
        <v-card-title class="admin-card-title">
          <div class="admin-actions">
            <v-btn
              @click="newUser"
              elevate="4"
              small
              color="primary"
              v-if="isAdmin"
            >
              <v-icon left dark>
                mdi-account-plus
              </v-icon>
              {{ $t('admin.newRecord') }}
            </v-btn>
            <v-spacer v-else />
            <div
              v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm"
              class="admin-desktop-actions ml-2"
            >
              <v-btn
                small
                :disabled="!selected.length"
                color="accent"
                class="ma-2"
                @click="copyEmails"
              >
                <v-icon left dark v-if="copied">
                  mdi-clipboard-check-multiple
                </v-icon>
                <v-icon left dark v-else>
                  mdi-clipboard-text-multiple
                </v-icon>
                {{ $t('admin.copyEmails') }}
              </v-btn>
              <v-btn
                small
                color="accent"
                class="ma-2"
                @click="exportExcel"
                :disabled="!selected.length"
              >
                <v-icon left dark>
                  mdi-file-excel
                </v-icon>
                {{ $t('admin.export') }}
              </v-btn>
              <v-btn small color="accent" class="ma-2" @click="openFilters">
                <v-icon left dark>
                  mdi-filter
                </v-icon>
                {{ $t('admin.filter') }}
              </v-btn>
              <v-btn
                small
                color=""
                class="ma-2"
                @click="clearFilters"
                v-show="dirtyFilters"
              >
                <v-icon left color="accent">
                  mdi-filter-remove
                </v-icon>
                {{ $t('admin.clearFilters') }}
              </v-btn>

              <v-btn small color="accent" class="ma-2" @click="openCalendar">
                <v-icon left dark>
                  mdi-calendar
                </v-icon>
                {{ $t('admin.calendar') }}
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="openNotifications"
                  >
                    <v-icon :color="showNotification ? 'primary' : ''">
                      mdi-bell
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('admin.nextEvents') }}</span>
              </v-tooltip>
            </div>
            <v-menu bottom left v-else>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="secondary" icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item>
                  <v-list-item-title
                    @click="copyEmails"
                    :disabled="!selected.length"
                  >
                    {{ $t('admin.copyEmails') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title @click="openFilters">
                    {{ $t('admin.filter') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-show="dirtyFilters">
                  <v-list-item-title @click="clearFilters">
                    {{ $t('admin.clearFilters') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title @click="openCalendar">
                    {{ $t('admin.calendar') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-text-field
            class="search-field"
            v-model="search"
            append-icon="mdi-magnify"
            :label="searchText"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <div class="data-loading" v-if="!leadersLoaded">
          <v-progress-circular
            :size="70"
            :width="7"
            color="blue"
            indeterminate
          ></v-progress-circular>
        </div>
        <v-data-table
          v-else
          class="admin-table"
          v-model="selected"
          show-select
          item-key="id"
          :headers="headers"
          :items="leaders"
          :items-per-page="-1"
          :search="search"
          :footer-props="footerProps"
          multi-sort
          :no-data-text="noDataText"
          :no-results-text="noResultsText"
          :mobile-breakpoint="0"
        >
          <template v-slot:item.actions="{ item }" v-if="leaders.length">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  @click="editItem(item)"
                  v-bind="attrs"
                  v-on="on"
                  v-if="isAdmin"
                  class="mr-2 item-action"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('common.edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  @click="deleteItem(item)"
                  v-bind="attrs"
                  v-on="on"
                  class="item-action"
                  v-if="isAdmin"
                >
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('common.delete') }}</span>
            </v-tooltip>
          </template>
          <template v-slot:no-data>
            <div class="no-data">
              <span v-if="dirtyFilters">
                {{ $t('admin.noFilterMatches') }}
              </span>
              <span v-else>{{ $t('admin.noDataText') }}</span>
            </div>
          </template>
          <template #footer.page-text="props">
            {{ props.pageStart }}-{{ props.pageStop }}{{ '  ' }} / {{ '  ' }}
            {{ props.itemsLength }}
          </template>
          <template v-slot:item.name="{ item }">
            <div class="name-container">
              <div class="mr-2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      @click="openProfile(item)"
                      class="mr-2 item-action"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-account
                    </v-icon>
                  </template>
                  <span>{{ $t('admin.seeProfile') }}</span></v-tooltip
                >
              </div>
              <div>
                <TextHighlight :queries="search">{{ item.name }}</TextHighlight>
              </div>
            </div>
          </template>
          <template v-slot:item.lastname="{ item }">
            <TextHighlight :queries="search">{{ item.lastname }}</TextHighlight>
          </template>
          <template v-slot:item.country="{ item }">
            <TextHighlight :queries="search">{{ item.country }}</TextHighlight>
          </template>
          <template v-slot:item.city="{ item }">
            <TextHighlight :queries="search">{{ item.city }}</TextHighlight>
          </template>
          <template v-slot:item.unities="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleUnities(item.unities)
            }}</TextHighlight>
          </template>
          <template v-slot:item.professions="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.professions)
            }}</TextHighlight>
          </template>
          <template v-slot:item.postGrades="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.postGrades)
            }}</TextHighlight>
          </template>
          <template v-slot:item.specialties="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.specialties)
            }}</TextHighlight>
          </template>
          <template v-slot:item.subSpecialties="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.subSpecialties)
            }}</TextHighlight>
          </template>
          <template v-slot:item.criteria="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.criteria)
            }}</TextHighlight>
          </template>
          <template v-slot:item.profiles="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.profiles)
            }}</TextHighlight>
          </template>
          <template v-slot:item.cientificAs="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.cientificAs)
            }}</TextHighlight>
          </template>
          <template v-slot:item.firstLanguage="{ item }">
            <TextHighlight :queries="search">{{
              item.firstLanguage
            }}</TextHighlight>
          </template>
          <template v-slot:item.secondLanguage="{ item }">
            <TextHighlight :queries="search">{{
              getVisibleValue(item.secondLanguage)
            }}</TextHighlight>
          </template>
          <template v-slot:item.range="{ item }">
            <TextHighlight :queries="search">{{ item.range }}</TextHighlight>
          </template>
          <template v-slot:item.phone="{ item }">
            <TextHighlight :queries="search">{{ item.phone }}</TextHighlight>
          </template>
          <template v-slot:item.idNumber="{ item }">
            <TextHighlight :queries="search">{{ item.idNumber }}</TextHighlight>
          </template>
          <template v-slot:item.email="{ item }">
            <TextHighlight :queries="search">{{ item.email }}</TextHighlight>
          </template>
          <template v-slot:item.observations="{ item }">
            <div class="observations-cell">
              <TextHighlight :queries="search">{{
                getVisibleValue(item.observations)
              }}</TextHighlight>
            </div>
          </template>
          <template v-slot:item.endContract="{ item }">
            <TextHighlight :queries="search">
              {{ getDateFromString(item.endContract) }}
            </TextHighlight>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <TextHighlight :queries="search">{{
              getDateFromTimestamp(item.createdAt)
            }}</TextHighlight>
          </template>
        </v-data-table>
      </v-card>
    </v-row>
    <input id="emailsSelected" type="text" />
    <!-- START Dialogs -->
    <LeaderForm v-if="isAdmin" />
    <LeaderProfile />
    <PdfDialog />
    <FiltersDialog />
    <CalendarDialog />
    <NotificationDialog />
    <v-dialog
      v-model="openRemoveDialog"
      max-width="477px"
      persistent
      v-if="isAdmin"
    >
      <v-card>
        <v-card-title class="headline">
          <span>
            {{ $t('admin.deleteConfirmation') }}
            <strong>{{ ' ' + leaderToDelete.name }}</strong> ?
          </span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeDelete">{{ $t('admin.noCancel') }}</v-btn>
          <v-btn color="primary" dark @click="deleteItemConfirm">
            {{ $t('admin.yesDelete') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- END Dialogs -->
  </div>
</template>

<script>
import TextHighlight from 'vue-text-highlight'
import LeaderForm from '@/components/LeaderForm'
import LeaderProfile from '@/components/LeaderProfile'
import PdfDialog from '@/components/PdfDialog'
import FiltersDialog from '@/components/FiltersDialog'
import CalendarDialog from '@/components/CalendarDialog'
import NotificationDialog from '@/components/NotificationDialog'
import { mapState } from 'vuex'
import {
  getVisibleValue,
  getVisibleUnities,
  getDateFromTimestamp,
  getDateFromString,
  isSessionValid,
  getExportableData,
  translateLeaders
} from '@/utils/common'
import { json2excel } from 'js2excel'
import _clone from 'lodash/cloneDeep'
import { auth } from '../firebase'
import { isUserAdmin } from '@/consts/common'

export default {
  components: {
    TextHighlight,
    LeaderForm,
    LeaderProfile,
    PdfDialog,
    FiltersDialog,
    CalendarDialog,
    NotificationDialog
  },
  data: () => ({
    search: '',
    selected: [],
    copied: false,
    showLoading: true,
    isAdmin: isUserAdmin(auth.currentUser.email),
    editedItem: -1,
    leaderToDelete: {}
  }),
  created() {
    this.$store.dispatch('leaderStore/fetchLeaders')
    if (this.isAdmin)
      this.headers.push({
        text: '',
        value: 'actions',
        sortable: false,
        width: '73px'
      })
  },
  computed: {
    ...mapState({
      leaders: state => translateLeaders(state.leaderStore.leaders),
      leadersMap: state => state.leaderStore.leadersMap,
      leadersLoaded: state => state.leaderStore.leadersLoaded,
      openRemoveDialog: state => state.leaderStore.openRemoveDialog,
      dirtyFilters: state => state.leaderStore.dirtyFilters,
      showNotification: state => state.eventStore.showNotification
    }),
    footerProps() {
      return {
        itemsPerPageText: this.$t('admin.itemsPerPage'),
        itemsPerPageAllText: this.$t('admin.all'),
        itemsPerPageOptions: [10, 20, 50, 100, -1]
      }
    },
    noDataText() {
      return this.$t('admin.noDataText')
    },
    noResultsText() {
      return this.$t('admin.noResultsText')
    },
    searchText() {
      return this.$t('common.search')
    },
    headers() {
      const headers = [
        { text: this.$t('fields.name'), value: 'name', width: '134px' },
        { text: this.$t('fields.lastname'), value: 'lastname', width: '115px' },
        { text: this.$t('fields.country'), value: 'country', width: '93px' },
        { text: this.$t('fields.city'), value: 'city', width: '110px' },
        { text: this.$t('fields.unity'), value: 'unities', width: '110px' },
        {
          text: this.$t('fields.profession'),
          value: 'professions',
          width: '110px'
        },
        {
          text: this.$t('fields.postgrade'),
          value: 'postGrades',
          width: '180px'
        },
        {
          text: this.$t('fields.specialty'),
          value: 'specialties',
          width: '150px'
        },
        {
          text: this.$t('fields.subSpecialty'),
          value: 'subSpecialties',
          width: '155px'
        },
        {
          text: this.$t('fields.criteria'),
          value: 'criteria',
          width: '210px'
        },
        { text: this.$t('fields.profile'), value: 'profiles', width: '132px' },
        {
          text: this.$t('fields.associations'),
          value: 'cientificAs',
          width: '350px'
        },
        {
          text: this.$t('fields.firstLang'),
          value: 'firstLanguage',
          width: '110px'
        },
        {
          text: this.$t('fields.secondLang'),
          value: 'secondLanguage',
          width: '111px'
        },
        { text: this.$t('fields.range'), value: 'range', width: '160px' },
        { text: this.$t('fields.mobile'), value: 'phone', width: '110px' },
        { text: this.$t('fields.id'), value: 'idNumber', width: '110px' },
        { text: this.$t('fields.email'), value: 'email', width: '110px' },
        {
          text: this.$t('fields.observations'),
          value: 'observations',
          width: '250px'
        },
        {
          text: this.$t('fields.createdDate'),
          value: 'createdAt',
          width: '142px'
        }
      ]

      if (this.isAdmin) {
        headers.splice(20, 0, {
          text: this.$t('fields.endContract'),
          value: 'endContract',
          width: '142px'
        })
      }

      return headers
    }
  },
  methods: {
    getVisibleValue: getVisibleValue,
    getVisibleUnities: getVisibleUnities,
    getDateFromTimestamp: getDateFromTimestamp,
    getDateFromString: getDateFromString,
    newUser: function() {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('leaderStore/openFormDialog', true)
    },
    openFilters: function() {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('leaderStore/openFiltersDialog', true)
    },
    openCalendar: function() {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('eventStore/openCalendarDialog', true)
    },
    openNotifications: function() {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('eventStore/openNotificationDialog', true)
    },
    exportExcel: function() {
      try {
        json2excel({
          data: getExportableData(_clone(this.selected), this.isAdmin),
          name: 'BD KOL - ' + new Date().toDateString().substring(3),
          formateDate: 'dd/mm/yyyy'
        })
      } catch (e) {
        console.error('export error')
      }
    },
    clearFilters: function() {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('leaderStore/clearFilters')
    },
    copyEmails: function() {
      let emails = []
      this.selected.map(item => {
        emails.push(item.email)
      })
      if (emails.length) {
        let aux = document.getElementById('emailsSelected')
        aux.setAttribute('value', emails)
        document.body.appendChild(aux)
        aux.select()
        document.execCommand('copy')
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 1500)
      }
    },
    openProfile(leader) {
      if (!isSessionValid()) {
        location.reload()
        return
      }

      this.$store.dispatch('leaderStore/openProfile', {
        leader: this.leadersMap[leader.id]
      })
    },
    editItem(leader) {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.$store.dispatch('leaderStore/editLeader', {
        leader: this.leadersMap[leader.id]
      })
    },

    deleteItem(item) {
      if (!isSessionValid()) {
        location.reload()
        return
      }
      this.leaderToDelete = this.leadersMap[item.id] || item
      this.$store.dispatch('leaderStore/openRemoveDialog', true)
    },

    deleteItemConfirm() {
      this.$store.dispatch('leaderStore/deleteLeader', {
        leader: this.leaderToDelete
      })
      this.closeDelete()
    },

    closeDelete() {
      this.$store.dispatch('leaderStore/openRemoveDialog', false)
      setTimeout(() => {
        this.leaderToDelete = {}
      }, 800)
    }
  }
}
</script>
<style lang="scss">
.admin-container {
  padding: 0 50px;

  .admin-title {
    color: #cf671b;
    padding-bottom: 10px;

    @media only screen and (max-width: 599px) {
      font-size: 16px;
    }
    @media only screen and (max-width: 460px) {
      padding-top: 9%;
    }
  }

  .admin-card {
    width: 100%;

    .admin-card-title {
      justify-content: space-between;

      .admin-actions {
        @media only screen and (max-width: 599px) {
          width: 100%;
          display: flex;
          justify-content: space-between;
        }

        .admin-desktop-actions {
          display: inline-block;
        }
      }
      .search-field {
        max-width: 400px;
        padding: 0;
        margin: 0;
      }
    }
    .admin-table {
      .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
        font-size: 13px;
      }
      .name-container {
        display: flex;
        align-items: center;
      }
      .observations-cell {
        line-height: 18px;
        max-height: 90px;
        overflow-y: auto;
        word-break: break-word;
      }
      .item-action {
        color: #5885b7;
        &:hover {
          color: #31649e;
          transform: scale3d(1.3, 1.3, 1.3);
        }
      }
      .no-data {
        position: fixed;
        width: 100%;
        margin-top: -10px;
        font-size: 16px;
        color: #353535;
        font-weight: bold;
      }
    }
    .text__highlight {
      background-color: #adeeff;
    }
    .data-loading {
      margin: 40px auto;
      width: 70px;
    }
  }
  table > tbody > tr > td:nth-child(1),
  table > thead > tr > th:nth-child(1) {
    position: sticky !important;
    left: 0;
    z-index: 1;
    background: white;
  }
  table > thead > tr > th:nth-child(1) {
    z-index: 1;
  }

  table > tbody > tr > td:nth-child(2),
  table > thead > tr > th:nth-child(2) {
    position: sticky !important;
    left: 56px;
    z-index: 1;
    background: white;
  }
  table > tbody > tr > td:nth-child(2) {
    padding-left: 0 !important;
  }
  table > thead > tr > th:nth-child(2) {
    z-index: 1;
    padding-left: 30px !important;
  }

  #emailsSelected {
    opacity: 0;
  }
}
</style>
